import { EMIT_TIMEOUT, GLOBAL_SERVICE_NAME } from '../constants';

type EventListener<
  Args extends readonly unknown[] = readonly unknown[],
  R = void
> = (...args: Args) => R | Promise<R>;
type EventListenersContainer = Record<string, EventListener[]>;
type ServiceListenersContainer = Record<string, EventListenersContainer>;

export class EventEmitter {
  private readonly servicesListeners: ServiceListenersContainer = {
    [GLOBAL_SERVICE_NAME]: {}
  };

  on(
    eventName: string,
    listener: EventListener,
    serviceName = GLOBAL_SERVICE_NAME
  ) {
    let listeners: EventListenersContainer = {};

    if (!this.servicesListeners[serviceName]) {
      this.servicesListeners[serviceName] = listeners;
    } else {
      listeners = this.servicesListeners[serviceName];
    }

    if (!listeners[eventName]) {
      listeners[eventName] = [];
    }

    listeners[eventName].push(listener);

    return () => {
      const index = listeners[eventName].indexOf(listener);
      if (index > -1) {
        listeners[eventName].splice(index, 1);
      }
    };
  }

  async emit(
    eventName: string,
    eventArgs: unknown[] = [],
    serviceName = GLOBAL_SERVICE_NAME
  ) {
    const listeners = this.servicesListeners[serviceName];

    if (!listeners) {
      return;
    }

    const eventListeners = listeners[eventName] ?? [];
    const promises = eventListeners.map(async (listener) => {
      try {
        return await listener(...eventArgs);
      } catch (error) {
        console.error('Event listener error:', error);
        return;
      }
    });

    const allSettled = Promise.allSettled(promises);
    const timeout = new Promise<void>((resolve) =>
      setTimeout(resolve, EMIT_TIMEOUT)
    );

    return Promise.race([allSettled, timeout]);
  }

  unsubscribeEvent(eventName: string, serviceName = GLOBAL_SERVICE_NAME) {
    const listeners = this.servicesListeners[serviceName];

    if (!listeners) {
      return;
    }

    listeners[eventName] = [];
  }

  unsubscribeAllEvents(serviceName?: string) {
    if (!serviceName) {
      for (const serviceName of Object.keys(this.servicesListeners)) {
        if (serviceName) {
          this.unsubscribeAllEvents(serviceName);
        }
      }

      return;
    }

    const listeners = this.servicesListeners[serviceName];

    if (!listeners) {
      return;
    }

    for (const eventName of Object.keys(listeners)) {
      listeners[eventName] = [];
    }
  }
}
